import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { ModelBase } from "@models";
import { 
  Character, BasicCreatureInstance, 
} from "@dndModels";
import { 
  DATA_TYPE_INFO,
} from "@dndModels/Constants";

import FilterBox from "@dndComponents/filtering/FilterBox";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
// import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import { makeStyles } from '../../../../utilities/theme_utilities';

import LoadingBox from "@components/input/LoadingBox";
// import ButtonBox from '../../../../components/input/buttons/ButtonBox';
// import ToastBox from '../../../../components/display/ToastBox';
// import ExpandButton from '../../../../components/input/buttons/ExpandButton';
// import DisplayStringWithReplacements from '../display/DisplayStringWithReplacements';
// import CheckBox from '../../../../components/input/CheckBox';
import PageLinks from "@components/Navigation/PageLinks";
import SelectObjectIndexRow from "@dndComponents/select/SelectObjectIndexRow";
import DataUtilities from '@dndUtilities/data_utilities';
import { DataType } from '@dndModels/Types';

interface Props { 
  data_type: DataType;
  character?: Character | BasicCreatureInstance;
  blocked_ids?: string[];
  filter: any;
  true_filter?: any; // If this is set then it will run this filter before setting objects, which then will limit the options in the FilterBox. regular filter is applied before rendering objects, but not for what's passed to FilterBox
  getLinks?: boolean;
  onClick?: (obj: ModelBase) => void;
  updateFilter: (changed: any) => void;
  sort?: (obj1: ModelBase, obj2: ModelBase) => number;
  // variants_disabled?: boolean;
  log_it?: boolean;
  disableObj?: (obj: ModelBase) => { disabled: boolean, reasons: string };
  detailFormat?: (obj: ModelBase) => React.ReactNode;
  extra_filter?: (o: ModelBase) => boolean;
  extra_fields?: string[];
  quick_fields?: string[];
  values?: string[];
  reload_time?: number;
  onChange?: (changed: string[]) => void;
  show_image?: boolean;
  flat?: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function SelectObjectIndex({
  data_type,
  character,
  blocked_ids = [],
  filter,
  true_filter,
  getLinks = false,
  onClick,
  updateFilter,
  sort,
  // variants_disabled = false,
  log_it = false,
  disableObj,
  extra_filter,
  detailFormat,
  extra_fields,
  quick_fields,
  values,
  // reload_time,
  onChange,
  show_image = false,
  flat = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [page_num, setPageNum] = useState(0);
  const [objects, setObjects] = useState<ModelBase[]>([]);
  const [prevFilter, setPrevFilter] = useState(filter);
  const [prevDataType, setPrevDataType] = useState(data_type);

  const load = useCallback( async () => {
    if (log_it) {
      debugger;
    }
    if (character) { 
      const objects_of_type = await data_util.getGoodDataOfTypeAsync(data_type);
      // debugger;
      if (objects_of_type && objects_of_type.length > 0) {
        // console.log(objects_of_type.filter(o => o.is_archived));
        let new_objects = objects_of_type;
        if (true_filter) {
          new_objects = api.filter(data_type, objects_of_type, true_filter) as ModelBase[];
        }
        setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
      }
    } else {
      // console.log(filter);
      const res = await api.getObjects({ data_type, filter: true_filter });
      const new_objects = res as ModelBase[];
      setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
    }
    setLoading(false);
  }, [character, data_type, true_filter, log_it]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if(!common_util.objectsMatch(filter, prevFilter) ||
      data_type !== prevDataType) {
      setPrevFilter(filter);
      setPrevDataType(data_type);
    }
  }, [filter, data_type, prevFilter, prevDataType]);

  const sorter = (a: any, b: any) => {
    if (sort) {
      const sort_value = sort(a,b);
      if (sort_value !== 0) {
        return sort_value;
      }
    }
    if (a instanceof ModelBase && b instanceof ModelBase) {
      return a.use_name.trim().localeCompare(b.use_name.trim()); 
    }
    return 1;
  }

  const render = () => {
    const page_size = 15;
    let filtered = [...objects];
    if (log_it) {
      debugger;
    }
    if (character instanceof Character) {
      filtered = filtered.filter(o => 
        common_util.anyOverlaps(["",...character.c_options.source_books], o.source_book_ids));
      if (!character.c_options.allow_monster_feature_sources) {
        filtered = filtered.filter(o => !o.monster);
      }
    } 
    filtered = filtered.filter(o => !blocked_ids.includes(o._id));
    filtered = api.filter(data_type, filtered, filter);
    const variants = data_util.getAllVariants(filtered);
    if (!flat) {
      if (character) {
        filtered = data_util.getAllDominants(filtered);
      } else {
        filtered = filtered.filter(o => o.isDominant());
      }
    }
    if (extra_filter) {
      filtered = filtered.filter(o => extra_filter(o));
    }
    filtered = filtered.sort(sorter);
    const page_count = Math.ceil(filtered.length / page_size);
    const filtered_and_paged = filtered.slice(page_size * page_num, page_size * (page_num + 1));
    const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
    // console.log(objects);
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={3}>
          <span className="header">
            { dt_info?.plural??"" }
          </span>
        </Grid>
        <Grid size={9}>
          <FilterBox 
            the_filter={filter}
            data_type={data_type}
            extra_fields={extra_fields}
            quick_fields={quick_fields}
            flat
            all_objects={objects}
            dont_use_api
            onChange={updateFilter}
            // onLoad={updateFilter}
          />
        </Grid>
        <Grid size={12}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
        <Grid size={12}>
          { filtered_and_paged.map(obj => {
            const variant_finder = variants.filter(o => o.variant_group_id === obj.variant_group_id);
            return (
              <Grid
                key={obj._id}
                className="padded"
                size={12}>
                { renderModelBase(obj, variant_finder) }
              </Grid>
            );
          }) }
          <Grid size={12}>
            <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
          </Grid>
        </Grid>
      </Grid>
    );
  }
  
  const renderModelBase = (model_base: ModelBase, variants: ModelBase[]) => { 
    if (log_it) {
      console.log(model_base);
    }
    return (
      <SelectObjectIndexRow 
        char={character}
        data_type={data_type} 
        model_base={model_base} 
        variants={variants}
        getLinks={getLinks}
        onClick={onClick}
        disableObj={disableObj}
        detailFormat={detailFormat}
        values={values}
        onChange={onChange}
        show_image={show_image}
        log_it={log_it && model_base.name.includes("Necro")}
      />
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return render();
  }
}